<template>
  <div class="card">
    <div class="row mb-2">
      <div class="col-6 align-self-center">
        <h2 class="subtitle mb-0">{{ label }}</h2>
      </div>
      <div class="col-6 alinmov2 align-self-center">
        <base-button
          type="link"
          class="min-button"
          :disabled="!canAdd"
          @click="openCreate()"
        >
          <i class="flaticon-plus"></i> Add new
        </base-button>
      </div>
    </div>

    <div class="row">
      <div class="col-12 mb-4" v-if="!value.length">
        <base-alert type="warning" class="mb-1 info-min">
          <i class="flaticon-warning mr-1"></i>
          {{
            areaSelected
              ? "There are no locations here"
              : "Select an Area before adding locations"
          }}
        </base-alert>
      </div>

      <div class="col-12" v-else>
        <base-alert type="warning" class="mb-3 info-min" v-if="!hasDefault">
          <i class="flaticon-warning mr-1"></i>
          No default location: resellers will be required to choose one when
          booking.
        </base-alert>
        <div
          v-for="(location, index) in value"
          :key="location.id || `new-${index}`"
          class="card card-shadow pb-2"
        >
          <div class="row">
            <div
              class="col-xl-1 col-lg-1 col-md-1 col-sm-1 col-12 align-self-center alinmov6"
            >
              <i
                class="flaticon-placeholder location-icon"
                v-bind:class="{ 'text-danger': isOutsideArea(location) }"
              ></i>
            </div>

            <div
              class="col-xl-9 col-lg-9 col-md-9 col-sm-9 col-12 align-self-center alinmov4"
            >
              <div class="data-block">
                <h2 class="subtitle mb-0 d-md-inline d-sm-block">
                  {{ location.title }}
                  <span
                    class="badge-status badge-default ml-2"
                    v-if="location.isDefault"
                    >Default</span
                  >
                  <span
                    class="badge-status badge-danger ml-2"
                    v-if="isOutsideArea(location)"
                    >Outside area</span
                  >
                </h2>
                <span class="content-block">
                  <strong>Address:</strong> {{ getAddress(location) }}
                </span>
                <span class="content-block" v-if="location.shortDescription">
                  <strong>Short Description:</strong>
                  {{ location.shortDescription }}
                </span>
              </div>
            </div>

            <div
              class="col-xl-2 col-lg-2 col-md-2 col-sm-2 col-12 align-self-center mb-3 alinmov6"
            >
              <el-tooltip content="Edit" placement="bottom">
                <span class="icon-action ml-2" @click="openEdit(index)">
                  <i class="flaticon-pencil text-primary"></i>
                </span>
              </el-tooltip>

              <el-tooltip
                :content="location.isDefault ? 'Remove default' : 'Mark by default'"
                placement="bottom"
              >
                <span class="icon-action ml-2" @click="toggleDefault(index)">
                  <i
                    class="flaticon-bold-button"
                    v-bind:class="location.isDefault ? 'text-muted' : 'text-new'"
                  ></i>
                </span>
              </el-tooltip>

              <el-tooltip content="Delete" placement="bottom">
                <span class="icon-action ml-2" @click="removeLocation(index)">
                  <i class="flaticon-delete text-danger"></i>
                </span>
              </el-tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal
      :show.sync="showModal"
      modal-classes="modal-dialog-centered modal-md"
      body-classes
      @close="closeModal"
      no-close-on-backdrop
    >
      <template slot="header">
        <h5 class="subtitle">{{ modalTitle }}</h5>
      </template>

      <div class="row align-items-center mt-3 forms">
        <div class="col-12">
          <label class="title-label">Search by</label>
          <b-form-group v-slot="{ ariaDescribedby }" class="mb-3">
            <b-form-radio-group
              v-model="searchType"
              :aria-describedby="ariaDescribedby"
              name="searchType"
              buttons
              button-variant="outline-neutral"
              size="md"
            >
              <b-form-radio value="address" class="pc-5">Address</b-form-radio>
              <b-form-radio value="airport" class="pc-5">Airport</b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </div>

        <div class="col-12">
          <WSelect
            label="Address"
            :placeholder="searchPlaceholder"
            filterable
            remote
            reserve-keyword
            :remote-method="searchPlaces"
            :loading="loading"
            v-model="search"
            @change="selectPlace"
            :no-data-text="searchPlaceholder"
            allText
            markrequired
            :error="addressError"
          >
            <el-option
              v-for="(option, index) in options"
              :value="option.select"
              :label="option.select"
              :key="`${index}`"
            >
              <div class="d-flex align-items-center">
                <i :class="getIcon(option.type)"></i>
                <span class="ml-3">
                  {{ option.select }}
                </span>
              </div>
            </el-option>
          </WSelect>
        </div>

        <div class="col-12">
          <base-input
            v-model="location.title"
            label="Title"
            placeholder="Hotel lobby"
            maxlength="100"
            markrequired
            :error="titleError"
          />
        </div>

        <div class="col-12">
          <label class="title-label">Short Description</label>
          <textarea
            class="input-custom form-control"
            v-model="location.shortDescription"
            maxlength="250"
            rows="2"
            placeholder="E.g. Wait at the hotel lobby, the driver will hold a sign"
          ></textarea>
        </div>
      </div>

      <template slot="footer">
        <base-button
          type="outline-neutral"
          class="min-button mb-0"
          @click="closeModal"
          ><i class="flaticon-cancel"></i>Cancel</base-button
        >
        <base-button
          type="primary"
          class="min-button ml-auto mb-0"
          @click="saveLocation"
          ><i class="flaticon-floppy-disk"></i>
          {{ isEditing ? "Update" : "Save" }}</base-button
        >
      </template>
    </modal>
  </div>
</template>

<script>
import lodash from "lodash";
import { CountriesAPI, LocationsAPI } from "../../../../../core/api";
import WSelect from "@/components/Select";
import {
  buildLocationFromCustomPlace,
  buildLocationFromGooglePlace,
  buildOctoPlace,
  getPlaceIcon
} from "../services/place.service";
import {
  MAX_PICKUP_DROPOFF_LOCATIONS,
  addLocation,
  replaceLocationAt,
  removeLocationAt,
  toggleDefaultLocation,
  hasDefaultLocation,
  hasValidPlace,
  isLocationOutsideArea,
  getLocationAddress
} from "../services/pickup-dropoff.service";

const emptyLocation = () => ({
  title: "",
  shortDescription: "",
  isDefault: false,
  place: null
});

export default {
  name: "LocationListEditor",
  props: {
    value: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: "Locations"
    },
    areaSelected: {
      type: Boolean,
      default: false
    },
    areaPolygon: {
      type: Array,
      default: null
    },
    defaultSearchType: {
      type: String,
      default: "address"
    }
  },
  data() {
    return {
      showModal: false,
      editIndex: -1,
      location: emptyLocation(),
      search: "",
      searchType: "address",
      options: [],
      loading: false,
      submitted: false
    };
  },
  computed: {
    canAdd() {
      return (
        this.areaSelected && this.value.length < MAX_PICKUP_DROPOFF_LOCATIONS
      );
    },
    isEditing() {
      return this.editIndex > -1;
    },
    searchPlaceholder() {
      return this.searchType === "airport"
        ? "Airport name, IATA code or city"
        : "Address or Hotel Name";
    },
    hasDefault() {
      return hasDefaultLocation(this.value);
    },
    modalTitle() {
      return this.isEditing ? "Edit Location" : "New Location";
    },
    titleError() {
      return this.submitted && !this.location.title.trim()
        ? "Enter a Title"
        : "";
    },
    addressError() {
      if (!this.submitted && !this.location.place) return "";
      if (!hasValidPlace(this.location.place)) {
        return "Select an address with coordinates and country";
      }
      if (this.isOutsideArea(this.location)) {
        return "The address is outside the product area";
      }
      return "";
    }
  },
  watch: {
    searchType() {
      this.options = [];
    }
  },
  methods: {
    getIcon(type) {
      return getPlaceIcon(type);
    },
    getAddress(location) {
      return getLocationAddress(location);
    },
    isOutsideArea(location) {
      return isLocationOutsideArea(location, this.areaPolygon);
    },
    openCreate() {
      this.openModal(-1, emptyLocation());
    },
    openEdit(index) {
      this.openModal(index, lodash.cloneDeep(this.value[index]));
    },
    openModal(index, location) {
      this.editIndex = index;
      this.location = location;
      this.search = getLocationAddress(location);
      this.searchType = this.defaultSearchType;
      this.options = [];
      this.submitted = false;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.submitted = false;
    },
    async searchPlaces(input) {
      if (!input || input.length < 3) {
        this.options = [];
        return;
      }
      try {
        this.loading = true;
        let { data } = await CountriesAPI.autocompleteLocations(
          input,
          this.searchType === "airport" ? "airport" : "point_of_interest"
        );
        this.options = data.code === 200 ? data.data : [];
      } catch (error) {
        this.options = [];
      } finally {
        this.loading = false;
      }
    },
    async getAddressFromPoint(point) {
      if (point.owner !== "google") return buildLocationFromCustomPlace(point);
      try {
        let { data } = await LocationsAPI.getPlaceDetailsByPlaceId(
          point.place_id
        );
        return data.status
          ? buildLocationFromGooglePlace(data.data, point)
          : buildLocationFromCustomPlace(point);
      } catch (error) {
        return buildLocationFromCustomPlace(point);
      }
    },
    async selectPlace(value) {
      const point = this.options.find(option => option.select === value);
      if (!point) return;
      const address = await this.getAddressFromPoint(point);
      this.location.place = buildOctoPlace(address);
      if (!this.location.title) {
        this.location.title = address.name || "";
      }
    },
    saveLocation() {
      this.submitted = true;
      if (this.titleError || this.addressError) return;

      const location = {
        ...lodash.cloneDeep(this.location),
        title: this.location.title.trim()
      };
      this.$emit(
        "input",
        this.isEditing
          ? replaceLocationAt(this.value, this.editIndex, location)
          : addLocation(this.value, location)
      );
      this.closeModal();
    },
    removeLocation(index) {
      this.$emit("input", removeLocationAt(this.value, index));
    },
    toggleDefault(index) {
      this.$emit("input", toggleDefaultLocation(this.value, index));
    }
  },
  components: {
    WSelect
  }
};
</script>

<style scoped>
.location-icon {
  font-size: 28px;
}
</style>
